import { Component, Input, OnInit, OnDestroy, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormGroup, ReactiveFormsModule, FormBuilder, FormsModule, Validators } from '@angular/forms';
import { SelectButtonModule } from 'primeng/selectbutton';
import { FileUploadModule } from 'primeng/fileupload';
import { SafeResourceUrl, DomSanitizer } from '@angular/platform-browser';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import { DividerModule } from 'primeng/divider';
import { CheckboxModule } from 'primeng/checkbox';
import { InputNumberModule } from 'primeng/inputnumber';
import { OnboardingService } from '../../../../services/onboarding/onboarding.service';
import { TestScoreRequest } from '../../../../models/onboarding/test-scores';
import { MessageService } from 'primeng/api';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { BlockUIModule } from 'primeng/blockui';
import { KeyFilterModule } from 'primeng/keyfilter';
import { ToastModule } from 'primeng/toast';
import { LinkdindetailsFetchComponent, FieldStatus } from '../linkdindetails-fetch/linkdindetails-fetch.component';
import { validateFile } from '../../../../direct-apply/shared/validators/form-validation.utils';

export interface UploadedFileItem {
  file?: File;
  previewUrl: string | SafeResourceUrl;
  name: string;
  type: string;
  base64Data?: string;
  isExistingUrl?: boolean;
}

@Component({
  selector: 'app-test-scores',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SelectButtonModule,
    FileUploadModule,
    DialogModule,
    ButtonModule,
    InputTextModule,
    CheckboxModule,
    InputNumberModule,
    TooltipModule,
    DividerModule,
    ProgressSpinnerModule,
    BlockUIModule,
    KeyFilterModule,
    ToastModule,
    LinkdindetailsFetchComponent
  ],
  providers: [MessageService],
  templateUrl: './test-scores.component.html',
  styleUrls: ['./test-scores.component.scss']
})
export class TestScoresComponent implements OnInit, OnDestroy {
  @Input() parentForm!: FormGroup;
  @Output() stepSaved = new EventEmitter<void>();
  @Output() saveFailed = new EventEmitter<void>();
  @ViewChild('hiddenFileInput') hiddenFileInput!: ElementRef<HTMLInputElement>;

  testScoresForm!: FormGroup;
  displayPreviewDialog: boolean = false;
  selectedPreviewItem: UploadedFileItem | null = null;
  safePdfUrl: SafeResourceUrl | null = null;

  numericRegex: RegExp = /^[0-9]+(\.[0-9]+)?$/;
  isPreviewLoading: boolean = false;

  // Document-reading modal state
  @ViewChild(LinkdindetailsFetchComponent) docReadingModal!: LinkdindetailsFetchComponent;
  isDocModalVisible: boolean = false;
  docModalFileName: string = '';
  activeDocModalField: string = '';
  /** Which score field is targeted for auto-fill, and the pending data until user clicks Continue */
  private activeScoreField: string = '';
  private pendingExtractedData: any = null;

  /** Fields shown inside the modal while a test score document is being parsed */
  readonly testScoreDocFields: FieldStatus[] = [
    { id: 'ts-f0', name: 'Test Type',       status: 'found',   statusId: 'ts-s0' },
    { id: 'ts-f1', name: 'Overall Score',    status: 'found',   statusId: 'ts-s1' },
    { id: 'ts-f2', name: 'Section Scores',   status: 'reading', statusId: 'ts-s2' },
    { id: 'ts-f3', name: 'Test Date',        status: 'queued',  statusId: 'ts-s3' }
  ];

  studentId: string = '';
  existingId: number | null = null;
  savingInProgress: boolean = false;
  isLoading: boolean = false;

  elpOptions = [
    { label: 'Yes', value: true },
    { label: 'No', value: false }
  ];

  constructor(
    private fb: FormBuilder,
    private sanitizer: DomSanitizer,
    private onboardingService: OnboardingService,
    private messageService: MessageService
  ) { }

  currentUploadField: string | null = null;

  // All score-doc pairs used for watching & validation
  private readonly scorePairs = [
    { scoreField: 'ieltsScore', docField: 'ieltsDoc' },
    { scoreField: 'toeflScore', docField: 'toeflDoc' },
    { scoreField: 'duolingoScore', docField: 'duolingoDoc' },
    { scoreField: 'satScore', docField: 'satDoc' },
    { scoreField: 'apScore', docField: 'apDoc' },
    { scoreField: 'actScore', docField: 'actDoc' },
    { scoreField: 'greScore', docField: 'greDoc' },
    { scoreField: 'gmatScore', docField: 'gmatDoc' },
  ];

  ngOnInit() {
    this.isLoading = true;
    this.testScoresForm = this.fb.group({
      hasELP: [null, Validators.required],
      hasStandardizedTest: [null, Validators.required],
      ieltsScore: ['', [Validators.pattern(this.numericRegex)]],
      toeflScore: ['', [Validators.pattern(this.numericRegex)]],
      duolingoScore: ['', [Validators.pattern(this.numericRegex)]],
      satScore: ['', [Validators.pattern(this.numericRegex)]],
      apScore: ['', [Validators.pattern(this.numericRegex)]],
      greScore: ['', [Validators.pattern(this.numericRegex)]],
      gmatScore: ['', [Validators.pattern(this.numericRegex)]],
      actScore: ['', [Validators.pattern(this.numericRegex)]],
      ieltsDoc: [null],
      toeflDoc: [null],
      duolingoDoc: [null],
      satDoc: [null],
      apDoc: [null],
      greDoc: [null],
      gmatDoc: [null],
      actDoc: [null]
    });

    // ── Reactive error clearing ──────────────────────────────────────────
    // When a score is changed/cleared, clear docRequired on its paired doc field.
    // When a doc is changed/removed, clear scoreRequired on its paired score field.
    this.scorePairs.forEach(({ scoreField, docField }) => {
      this.testScoresForm.get(scoreField)?.valueChanges.subscribe(val => {
        const docControl = this.testScoresForm.get(docField);
        const scoreControl = this.testScoresForm.get(scoreField);

        // Score cleared → remove docRequired from doc field
        if (!val || val.toString().trim() === '') {
          if (docControl?.hasError('docRequired')) {
            docControl.setErrors(null);
          }
        }

        // Always remove scoreRequired from self when value changes
        if (scoreControl?.hasError('scoreRequired')) {
          const errors = { ...scoreControl.errors };
          delete errors['scoreRequired'];
          scoreControl.setErrors(Object.keys(errors).length ? errors : null);
        }
      });

      this.testScoresForm.get(docField)?.valueChanges.subscribe(val => {
        const scoreControl = this.testScoresForm.get(scoreField);
        const docControl = this.testScoresForm.get(docField);

        // Doc removed → clear docRequired from doc field itself
        if (!val) {
          if (docControl?.hasError('docRequired')) {
            docControl.setErrors(null);
          }
        }

        // Always remove scoreRequired from paired score field when doc changes
        if (scoreControl?.hasError('scoreRequired')) {
          const errors = { ...scoreControl.errors };
          delete errors['scoreRequired'];
          scoreControl.setErrors(Object.keys(errors).length ? errors : null);
        }
      });
    });
    // ────────────────────────────────────────────────────────────────────

    // ✓ FIX: Clear fields if ELP toggled off - but ONLY if hasELP was explicitly set to false
    this.testScoresForm.get('hasELP')?.valueChanges.subscribe(val => {
      // Only clear if explicitly false, not on initialization
      if (val === false) {
        const elpOnlyFields = ['ieltsScore', 'toeflScore', 'duolingoScore', 'ieltsDoc', 'toeflDoc', 'duolingoDoc'];
        elpOnlyFields.forEach(field => {
          this.testScoresForm.get(field)?.setValue(null, { emitEvent: false });
          this.testScoresForm.get(field)?.setErrors(null);
        });
        // NOTE: SAT is NOT cleared here because SAT can be used in both ELP and Standardized Test contexts
      }
      this.testScoresForm.updateValueAndValidity({ emitEvent: false });
    });

    // ✓ FIX: Clear fields if Standardized toggled off
    this.testScoresForm.get('hasStandardizedTest')?.valueChanges.subscribe(val => {
      if (val === false) {
        const stdOnlyFields = ['apScore', 'actScore', 'greScore', 'gmatScore', 'apDoc', 'actDoc', 'greDoc', 'gmatDoc'];
        stdOnlyFields.forEach(field => {
          this.testScoresForm.get(field)?.setValue(null, { emitEvent: false });
          this.testScoresForm.get(field)?.setErrors(null);
        });
        // NOTE: SAT is NOT cleared here either
      }
      this.testScoresForm.updateValueAndValidity({ emitEvent: false });
    });

    this.parentForm.setControl('testScores', this.testScoresForm);

    const storedData = localStorage.getItem('account');
    if (storedData) {
      const account = JSON.parse(storedData);
      this.studentId = account.userId || account.id || '';
      if (this.studentId) {
        this.loadExistingData();
      } else {
        this.isLoading = false;
      }
    } else {
      this.isLoading = false;
    }
  }

  loadExistingData() {
    this.isLoading = true;
    this.onboardingService.getTestScores(this.studentId).subscribe({
      next: (res: any) => {
        const data = Array.isArray(res) ? res[0] : (res?.result?.[0] || res);
        if (data && (data.id || data.Id)) {
          this.existingId = data.id || data.Id;
          this.patchForm(data);
        }
        this.isLoading = false;
      },
      error: (err) => {
        console.error('Failed to load test scores', err);
        this.isLoading = false;
      }
    });
  }

  patchForm(data: any) {
    const isEngNotGiven = data.isEnglishProficiencyTestNotGiven;
    let hasELP = !isEngNotGiven;

    let patchObj: any = {
      hasELP: hasELP,
      hasStandardizedTest: false
    };

    if (hasELP) {
      if (data.ieltsScores?.score) {
        patchObj.ieltsScore = data.ieltsScores.score;
        if (data.ieltsScores.documentUrl) patchObj.ieltsDoc = this.buildFileItemFromUrl(data.ieltsScores.documentUrl);
      }
      if (data.toeflScores?.score) {
        patchObj.toeflScore = data.toeflScores.score;
        if (data.toeflScores.documentUrl) patchObj.toeflDoc = this.buildFileItemFromUrl(data.toeflScores.documentUrl);
      }
      if (data.duolingoScores?.score) {
        patchObj.duolingoScore = data.duolingoScores.score;
        if (data.duolingoScores.documentUrl) patchObj.duolingoDoc = this.buildFileItemFromUrl(data.duolingoScores.documentUrl);
      }
    }

    if (data.satScores?.score) {
      patchObj.satScore = data.satScores.score;
      patchObj.hasStandardizedTest = true;
      if (data.satScores.documentUrl) patchObj.satDoc = this.buildFileItemFromUrl(data.satScores.documentUrl);
    }
    if (data.greScores?.score) {
      patchObj.greScore = data.greScores.score;
      patchObj.hasStandardizedTest = true;
      if (data.greScores.documentUrl) patchObj.greDoc = this.buildFileItemFromUrl(data.greScores.documentUrl);
    }
    if (data.gmatScores?.score) {
      patchObj.gmatScore = data.gmatScores.score;
      patchObj.hasStandardizedTest = true;
      if (data.gmatScores.documentUrl) patchObj.gmatDoc = this.buildFileItemFromUrl(data.gmatScores.documentUrl);
    }
    if (data.actScores?.score) {
      patchObj.actScore = data.actScores.score;
      patchObj.hasStandardizedTest = true;
      if (data.actScores.documentUrl) patchObj.actDoc = this.buildFileItemFromUrl(data.actScores.documentUrl);
    }
    if (data.apExams && data.apExams.length > 0) {
      patchObj.apScore = data.apExams[0].subjectScore;
      patchObj.hasStandardizedTest = true;
      if (data.apExams[0].documentUrl) patchObj.apDoc = this.buildFileItemFromUrl(data.apExams[0].documentUrl);
    }

    this.testScoresForm.patchValue(patchObj, { emitEvent: false });
  }

  private buildFileItemFromUrl(url: string): UploadedFileItem {
    const urlWithoutParams = url.split('?')[0].toLowerCase();
    let type = 'application/octet-stream';
    if (urlWithoutParams.endsWith('.pdf')) {
      type = 'application/pdf';
    } else if (['.jpg', '.jpeg', '.png', '.webp'].some(ext => urlWithoutParams.endsWith(ext))) {
      type = 'image/jpeg';
    }
    const name = url.split('?')[0].split('/').pop() || 'document';
    return { previewUrl: url, name, type, isExistingUrl: true };
  }

  private extractDocUrlFromItem(item: UploadedFileItem | null | undefined): string | null {
    if (!item) return null;
    if (item.base64Data) return item.base64Data;
    if (item.isExistingUrl) return typeof item.previewUrl === 'string' ? item.previewUrl : null;
    return null;
  }

  /**
   * Validates score-document pairs.
   * - Sets inline errors (docRequired / scoreRequired) with markAsTouched so they render immediately.
   * - Shows a single combined toast listing all sections that have no complete pair.
   */
  private validateScoreDocumentPairs(): boolean {
    const vals = this.testScoresForm.value;
    let isValid = true;

    const elpScores = this.scorePairs.slice(0, 4); // ielts, toefl, duolingo, sat
    const stdScores = this.scorePairs.slice(4);    // ap, act, gre, gmat

    // Clear all existing custom errors first
    this.scorePairs.forEach(item => {
      const scoreCtrl = this.testScoresForm.get(item.scoreField);
      const docCtrl = this.testScoresForm.get(item.docField);
      scoreCtrl?.setErrors(scoreCtrl.hasError('pattern') ? { pattern: true } : null);
      docCtrl?.setErrors(null);
    });

    const missingSections: string[] = [];

    // ── ELP validation ───────────────────────────────────────────────────
    if (vals.hasELP) {
      let hasAnyElpCompletePair = false;
      let hasElpInlineError = false;

      for (const item of elpScores) {
        const score = vals[item.scoreField];
        const doc = vals[item.docField];
        const hasScore = score && score.toString().trim() !== '';
        const hasDoc = doc !== null && doc !== undefined;

        if (!hasScore && hasDoc) {
          const existing = this.testScoresForm.get(item.scoreField)?.errors || {};
          this.testScoresForm.get(item.scoreField)?.setErrors({ ...existing, scoreRequired: true });
          this.testScoresForm.get(item.scoreField)?.markAsTouched();
          isValid = false;
          hasElpInlineError = true;
        }
        if (hasScore) {
          hasAnyElpCompletePair = true;
        }
      }

      // Only add to toast if no inline errors and no complete pair found
      if (!hasAnyElpCompletePair && !hasElpInlineError) {
        missingSections.push('English Language Proficiency Scores');
      }
    }

    // ── Standardized Test validation ─────────────────────────────────────
    if (vals.hasStandardizedTest) {
      let hasAnyStdCompletePair = false;
      let hasStdInlineError = false;

      for (const item of stdScores) {
        const score = vals[item.scoreField];
        const doc = vals[item.docField];
        const hasScore = score && score.toString().trim() !== '';
        const hasDoc = doc !== null && doc !== undefined;

        if (!hasScore && hasDoc) {
          const existing = this.testScoresForm.get(item.scoreField)?.errors || {};
          this.testScoresForm.get(item.scoreField)?.setErrors({ ...existing, scoreRequired: true });
          this.testScoresForm.get(item.scoreField)?.markAsTouched();
          isValid = false;
          hasStdInlineError = true;
        }
        if (hasScore) {
          hasAnyStdCompletePair = true;
        }
      }

      if (!hasAnyStdCompletePair && !hasStdInlineError) {
        missingSections.push('Standardized Test Scores');
      }
    }

    // Single combined toast listing ALL missing sections at once
    if (missingSections.length > 0) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: `Please fill details of at least one exam under ${missingSections.join(' and ')}.`,
        life: 5000
      });
      return false;
    }

    return isValid;
  }

  buildPayload(): TestScoreRequest {
    const vals = this.testScoresForm.value;

    const buildScoreObj = (score: string, docItem: any) => {
      if (!score || score.toString().trim() === '') return null;
      return {
        score: parseFloat(score) || 0,
        documentUrl: this.extractDocUrlFromItem(docItem)
      };
    };

    const hasStdScore = !!vals.satScore || !!vals.greScore || !!vals.gmatScore || !!vals.apScore || !!vals.actScore;

    const payload: any = {
      isStanardizedTestNotGiven: !hasStdScore,
      satScores: buildScoreObj(vals.satScore, vals.satDoc),
      actScores: buildScoreObj(vals.actScore, vals.actDoc),
      greScores: buildScoreObj(vals.greScore, vals.greDoc),
      gmatScores: buildScoreObj(vals.gmatScore, vals.gmatDoc),
      isEnglishProficiencyTestNotGiven: !vals.hasELP,
      toeflScores: buildScoreObj(vals.toeflScore, vals.toeflDoc),
      ieltsScores: buildScoreObj(vals.ieltsScore, vals.ieltsDoc),
      duolingoScores: buildScoreObj(vals.duolingoScore, vals.duolingoDoc),
      apExams: (vals.apScore && vals.apScore.toString().trim() !== '') ? [{
        subjectName: "AP",
        subjectScore: parseFloat(vals.apScore) || 0,
        documentUrl: this.extractDocUrlFromItem(vals.apDoc)
      }] : null
    };

    if (this.existingId) payload.id = this.existingId;
    if (this.studentId) payload.applicationUserId = this.studentId;

    return payload as TestScoreRequest;
  }

  saveData(): void {
    if (this.savingInProgress) return;
    if (this.existingId && !this.testScoresForm.dirty) {
      this.messageService.add({ severity: 'info', summary: 'No Changes', detail: 'No changes detected. Nothing to update.' });
      this.stepSaved.emit();
      return;
    }

    this.testScoresForm.markAllAsTouched();

    // Basic form validity (pattern errors etc.)
    if (this.testScoresForm.invalid) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Please fill in all required fields properly.',
        life: 5000
      });
      this.saveFailed.emit();
      return;
    }

    // Score-document pair validation
    if (!this.validateScoreDocumentPairs()) {
      this.saveFailed.emit();
      return;
    }

    this.savingInProgress = true;
    this.isLoading = true;
    const payload = this.buildPayload();

    if (this.existingId) {
      this.onboardingService.updateTestScore(payload).subscribe({
        next: () => {
          this.savingInProgress = false;
          this.isLoading = false;
          this.testScoresForm.markAsPristine();
          this.stepSaved.emit();
        },
        error: err => {
          console.error('Update failed', err);
          this.savingInProgress = false;
          this.isLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to update test scores.' });
          this.saveFailed.emit();
        }
      });
    } else {
      this.onboardingService.createTestScore(payload).subscribe({
        next: (res: any) => {
          this.savingInProgress = false;
          this.isLoading = false;
          const newId = res?.id || res?.result?.id;
          if (newId) this.existingId = newId;
          this.testScoresForm.markAsPristine();
          this.stepSaved.emit();
        },
        error: err => {
          console.error('Create failed', err);
          this.savingInProgress = false;
          this.isLoading = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to save test scores.' });
          this.saveFailed.emit();
        }
      });
    }
  }

  onFileUpload(event: any, fieldName: string) {
    if (event.files?.length > 0) {
      const file = event.files[0];
      const check = validateFile(file);
      if (!check.valid) {
        this.messageService.add({ severity: 'error', summary: 'Invalid File', detail: check.message });
        return;
      }
      this.processFile(file, fieldName);
    }
  }

  parseJobData: { [key: string]: { jobId: string, status: string, pollCount: number } } = {};
  private pollTimeouts: any[] = [];

  ngOnDestroy() {
    this.pollTimeouts.forEach(t => clearTimeout(t));
  }

  processFile(file: File, fieldName: string) {
    const reader = new FileReader();
    reader.onload = (e: any) => {
      const previewUrl: string | SafeResourceUrl =
        file.type === 'application/pdf'
          ? this.sanitizer.bypassSecurityTrustResourceUrl(URL.createObjectURL(file))
          : e.target.result;

      const newItem: UploadedFileItem = {
        file,
        previewUrl,
        name: file.name,
        type: file.type,
        base64Data: e.target.result,
        isExistingUrl: false
      };

      this.testScoresForm.get(fieldName)?.setValue(newItem);
      this.testScoresForm.get(fieldName)?.setErrors(null);
      this.testScoresForm.markAsDirty();
    };
    reader.readAsDataURL(file);

    this.submitDocumentForParsing(file, fieldName);
  }

  private submitDocumentForParsing(file: File, fieldName: string) {
    const fieldMapping: any = {
      satDoc: { type: 'SAT', scoreField: 'satScore' },
      ieltsDoc: { type: 'IELTS', scoreField: 'ieltsScore' },
      toeflDoc: { type: 'TOEFL', scoreField: 'toeflScore' },
      duolingoDoc: { type: 'DUOLINGO', scoreField: 'duolingoScore' },
      apDoc: { type: 'AP', scoreField: 'apScore' },
      actDoc: { type: 'ACT', scoreField: 'actScore' },
      greDoc: { type: 'GRE', scoreField: 'greScore' },
      gmatDoc: { type: 'GMAT', scoreField: 'gmatScore' }
    };

    const mapping = fieldMapping[fieldName];
    if (!mapping) return;

    // ── Show the document-reading modal ──────────────────────────────────────
    this.docModalFileName = file.name;
    this.activeDocModalField = fieldName;
    this.isDocModalVisible = true;

    setTimeout(() => {
      this.docReadingModal?.show(file.name);
    }, 0);
    // ─────────────────────────────────────────────────────────────────────────

    this.onboardingService.parseDocument(file, mapping.type).subscribe({
      next: (response) => {
        const jobId = response.job_id;
        this.parseJobData[fieldName] = { jobId, status: 'parsing', pollCount: 0 };
        this.activeScoreField = mapping.scoreField;
        this.startPollingForResult(jobId, fieldName, mapping.scoreField);
      },
      error: (err) => {
        console.error('Parse submission failed:', err);
        this.docReadingModal?.showError();
        this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to analyze document', life: 3000 });
      }
    });
  }

  private startPollingForResult(jobId: string, fieldName: string, scoreField: string) {
    const maxPolls = 30;
    
    const poll = () => {
      if (!this.parseJobData[fieldName]) return;
      this.parseJobData[fieldName].pollCount++;

      this.onboardingService.getDocumentParseResult(jobId).subscribe({
        next: (response) => {
          const status = response.status;
          if ((status === 'completed' || status === 'done') && response.result) {
            this.parseJobData[fieldName].status = 'completed';
            // Save temporarily until user clicks Continue
            this.pendingExtractedData = response.result;
            // Complete the modal (user clicks Continue to dismiss)
            if (this.activeDocModalField === fieldName) {
              this.docReadingModal?.completeNow();
            }
          } else if (status === 'failed' || status === 'error') {
            this.parseJobData[fieldName].status = 'error';
            if (this.activeDocModalField === fieldName) {
              this.docReadingModal?.showError();
            }
            this.messageService.add({ severity: 'error', summary: 'Error', detail: 'Failed to analyze document', life: 3000 });
          } else {
            if (this.parseJobData[fieldName].pollCount < maxPolls) {
              const timeout = setTimeout(poll, 2000);
              this.pollTimeouts.push(timeout);
            } else {
              this.parseJobData[fieldName].status = 'error';
              if (this.activeDocModalField === fieldName) {
                this.docReadingModal?.showError();
              }
              this.messageService.add({ severity: 'warn', summary: 'Timeout', detail: 'Document analysis took too long.', life: 3000 });
            }
          }
        },
        error: () => {
          if (this.parseJobData[fieldName].pollCount < maxPolls) {
            const timeout = setTimeout(poll, 2000);
            this.pollTimeouts.push(timeout);
          } else {
            this.parseJobData[fieldName].status = 'error';
            if (this.activeDocModalField === fieldName) {
              this.docReadingModal?.showError();
            }
          }
        }
      });
    };

    const timeout = setTimeout(poll, 1000);
    this.pollTimeouts.push(timeout);
  }

  // ── Modal event handlers ──────────────────────────────────────────────────
  /**
   * ✓ FIX: This fires when user clicks "Continue" in the modal.
   * NOW we auto-fill the form BEFORE closing the modal, ensuring data persists.
   */
  onDocModalComplete(): void {
    console.log('Modal Complete - activeScoreField:', this.activeScoreField, 'pendingData:', this.pendingExtractedData);
    
    // ✓ FIX: Auto-fill FIRST, then close the modal
    if (this.activeScoreField && this.pendingExtractedData) {
      this.autoFillScore(this.activeScoreField, this.pendingExtractedData);
      console.log('Auto-filled:', this.activeScoreField, 'with value:', this.testScoresForm.get(this.activeScoreField)?.value);
    }
    
    // Now close the modal
    this.isDocModalVisible = false;
    this.pendingExtractedData = null;
    this.activeDocModalField = '';
    this.activeScoreField = '';
    
    this.messageService.add({
      severity: 'success',
      summary: 'Success',
      detail: 'Document analyzed successfully! Score has been auto-filled.',
      life: 3000
    });
  }

  onDocModalCancel(): void {
    // Simply discard the extracted data without touching the form
    this.pendingExtractedData = null;
    this.isDocModalVisible = false;
    this.activeDocModalField = '';
    this.activeScoreField = '';
  }

  onDocModalClose(): void {
    this.isDocModalVisible = false;
    // NOTE: Do not clear pendingExtractedData or activeDocModalField here,
    // because linkdindetails-fetch emits onClose() right before onComplete() and onCancel().
    // Those methods will handle their own state cleanup.
  }
  // ─────────────────────────────────────────────────────────────────────────

  /**
   * ✓ FIX: Auto-fill with emitEvent: false to prevent cascading updates that clear data
   */
private autoFillScore(scoreField: string, extractedData: any) {
  const totalScore = extractedData.total_score || extractedData.overall_band_score || extractedData.score;
  if (!totalScore) return; // API se nahi aaya — field untouched rahegi

  const ctrl = this.testScoresForm.get(scoreField);
  if (!ctrl) return;

  // API se aaya — hamesha overwrite
  ctrl.setValue(totalScore, { emitEvent: false });
  ctrl.markAsDirty();
  ctrl.updateValueAndValidity({ emitEvent: false });
  this.testScoresForm.updateValueAndValidity({ emitEvent: false });
}

  onHiddenFileChange(event: any) {
    const files = event.target.files;
    if (files?.length > 0 && this.currentUploadField) {
      this.processFile(files[0] as File, this.currentUploadField);
    }
    event.target.value = '';
    this.currentUploadField = null;
  }

  triggerUpload(fieldName: string) {
    this.currentUploadField = fieldName;
    if (this.hiddenFileInput) {
      this.hiddenFileInput.nativeElement.click();
    }
  }

  /**
   * ✓ FIX: Remove file with proper cleanup - only clear paired score if user explicitly removes the file
   */
  removeFileObj(fieldName: string) {
    // Mark as touched so that when the doc valueChange fires, it knows this is a user action
    this.testScoresForm.get(fieldName)?.markAsTouched();
    this.testScoresForm.get(fieldName)?.setValue(null);
    this.testScoresForm.get(fieldName)?.updateValueAndValidity();
    this.testScoresForm.get(fieldName)?.markAsDirty();
    this.testScoresForm.markAsDirty();
  }

  hasFile(fieldName: string): boolean {
    return this.testScoresForm.get(fieldName)?.value !== null;
  }

  getDisplayFileName(fieldName: string, defaultName: string): string {
    const file = this.testScoresForm.get(fieldName)?.value;
    if (!file) return '';
    return defaultName + ' Document';
  }

  openPreview(item: any) {
    if (!item) return;
    if (typeof item === 'string') {
      const urlWithoutParams = item.split('?')[0];
      const name = urlWithoutParams.split('/').pop() || 'document';
      const type = urlWithoutParams.toLowerCase().endsWith('.pdf') ? 'application/pdf' : 'image/jpeg';
      item = { name, previewUrl: item, type, isExistingUrl: true };
    }

    this.selectedPreviewItem = item;
    this.isPreviewLoading = true;
    this.safePdfUrl = null;

    const isPdf = this.isPdfUrl(item);
    if (isPdf) {
      const url = item.previewUrl;
      if (typeof url === 'string') {
        if (url.startsWith('http')) {
          const urlStr = encodeURIComponent(url);
          this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            `https://docs.google.com/viewer?url=${urlStr}&embedded=true`
          );
        } else {
          this.safePdfUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
        }
      } else {
        this.safePdfUrl = url;
      }
    }
    this.displayPreviewDialog = true;
  }

  onPreviewLoad() {
    this.isPreviewLoading = false;
  }

  isImageUrl(item: any): boolean {
    if (!item || typeof item !== 'object') return false;
    return item.type?.startsWith('image/');
  }

  isPdfUrl(item: any): boolean {
    if (!item || typeof item !== 'object') return item?.type === 'application/pdf';
    return item.type === 'application/pdf';
  }

  getFileUrl(item: any): any {
    if (!item) return '';
    if (typeof item === 'object') return item.previewUrl;
    return item;
  }
}